import { Injectable, OnDestroy } from "@angular/core";
import { combineLatest, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import { Category, CategoryInfo } from "../../model/category";
import { Pricelist } from "../../model/pricelist";
import { FirebaseQuery } from "../firebase.query";
import { CategoriesService } from "./categories.service";
import { CategoriesState, CategoriesStore } from "./categories.store";
import { PricelistsQuery } from "../pricelists/pricelists.query";

@Injectable({ providedIn: "root" })
export class CategoriesQuery extends FirebaseQuery<CategoriesState> implements OnDestroy {
  private pricelistsSyncStarted = false;

  public ngOnDestroy(): void {

  }

  public categories$: Observable<Category[]> = this.selectAll({
    filterBy: c => !c.test || environment.toggles.testCategories,
    sortBy: "order"
  });

  public categoriesForRoutes$: Observable<Category[]> = combineLatest([
    this.categories$,
    this.pricelistsQuery.pricelists$
  ]).pipe(
    map(([categories, pricelists]) => [
      ...categories,
      ...pricelists.map(pricelist => this.toCategory(pricelist))
    ])
  );

  public topLevelCategories$: Observable<Category[]> = this.selectAll({
    filterBy: c => !c.parentPath && (!c.test || environment.toggles.testCategories),
    sortBy: "order"
  }).pipe(
    map(categories => {
      return categories.map(c => ({
        ...c,
        fullPath: c.parentPath ? `/category/${c.parentPath}/${c.path}` : `/category/${c.path}`
      }));
    })
  );

  public nestedCategories$: Observable<CategoryInfo[]> = this.topLevelCategories$.pipe(
    map(categories =>
      categories.map(c => ({
        category: c,
        parent: this.getEntity(c.id),
        subcategories$: this.getChildCategories$([c.path])
      }))
    )
  );

  constructor(
    protected store: CategoriesStore,
    protected service: CategoriesService,
    private pricelistsQuery: PricelistsQuery
  ) {
    super(store, service);
  }

  public syncRouteCollectionsUntilDestroyed(component: OnDestroy): void {
    this.syncCollectionUntilDestroyed(component);
    this.ensurePricelistsSynced();
  }

  public getChildCategories$(categoryPaths: string[]): Observable<CategoryInfo[]> {
    const categoryId = categoryPaths.join("/");

    if (categoryId === "school/uniform-pricelists") {
      this.ensurePricelistsSynced();
      return this.pricelistsQuery.pricelistsForCategory$(categoryId).pipe(
        map(pricelists => {
          return pricelists.map(p => {
            const category = this.toCategory(p);
            return {
              category,
              parent: this.getEntity(categoryId),
              subcategories$: this.getChildCategories$([...categoryPaths, p.id])
            };
          });
        })
      );
    }

    return this.selectAll({
      filterBy: c => c.parentPath === categoryId && (!c.test || environment.toggles.testCategories),
      sortBy: "order"
    }).pipe(
      map(categories => {
        const categoryInfos: CategoryInfo[] = categories.map(category => {
          return {
            category,
            parent: this.getEntity(categoryId),
            subcategories$: this.getChildCategories$([...categoryPaths, category.path])
          };
        });
        return categoryInfos;
      })
    );
  }

  private toCategory(pricelist: Pricelist): Category {
    return {
      id: pricelist.id,
      name: pricelist.categoryName,
      subtitle: pricelist.name,
      path: pricelist.slug,
      image: pricelist.image || "",
      resourceFilePath: pricelist.filePath,
      fullPath: `/category/${pricelist.parentPath}/${pricelist.slug}`,
      parentPath: pricelist.parentPath
    } as Category;
  }

  private ensurePricelistsSynced(): void {
    if (this.pricelistsSyncStarted) {
      return;
    }

    this.pricelistsSyncStarted = true;
    this.pricelistsQuery.syncCollectionUntilDestroyed(this);
  }
}
